import { Component, Inject, OnInit } from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Guest } from '@tv3/store/guest/guest.model'
import { MessageService } from '@tv3/store/message/message.service'
import { MessagingChannelsService, WhatsappMessageTemplate } from '@tokeet-frontend/message'
import { combineLatest, of } from 'rxjs'
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators'
import { DataDictService } from '@tv3/services/data-dict.service'
import {
  ActionFailed,
  Destroyable,
  isSomething,
  Toaster,
  untilDestroy,
  userFullname,
} from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { Store } from '@ngrx/store'
import { AddMessageComplete } from '@tv3/store/message/message.actions'
import { Message, MessageType } from '@tv3/store/message/message.model'
import * as moment from 'moment'
import * as R from 'ramda'
import { ChannelMessagePayload } from '@tv3/store/message/message.request'
import { AuthService } from '@tv3/services/auth.service'
import { VendorResponse } from '@tokeet-frontend/vendors'

@Component({
  selector: 'app-initial-whatsapp-dialog',
  templateUrl: './initial-whatsapp-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./initial-whatsapp-dialog.component.scss'],
})
export class InitialWhatsappDialogComponent extends Destroyable implements OnInit {
  guest: Guest
  vendor: VendorResponse
  get inquiry() {
    return this.data.inquiry
  }

  isLoading = true

  whatsappTemplate: WhatsappMessageTemplate
  compiledWhatsappTemplate: string
  compiledTemplateVariables: string

  constructor(
    public dialogRef: MatDialogRef<InitialWhatsappDialogComponent>,
    private store: Store<any>,
    private toaster: Toaster,
    private auth: AuthService,
    private msgService: MessageService,
    private messagingChannelsService: MessagingChannelsService,
    private dataDictService: DataDictService,
    @Inject(MAT_DIALOG_DATA) public data: { guest?: Guest; vendor?: VendorResponse; inquiry?: Inquiry }
  ) {
    super()
    this.guest = data.guest
    this.vendor = data.vendor
  }

  ngOnInit(): void {
    this.messagingChannelsService
      .getWhatsappTemplates()
      .pipe(
        map((templates) => {
          if (this.guest) {
            return this.getGuestWelcomeTemplate(templates, this.inquiry ? 'booking' : 'guest')
          }
          if (this.vendor) {
            return this.getVendorWelcomeTemplate(templates)
          }
          return null
        }),
        filter((template) => !!template),
        tap((t) => (this.whatsappTemplate = t)),
        switchMap((template) => this.compileWhatsappTemplate(template)),
        untilDestroy(this)
      )
      .subscribe(
        (data) => {
          this.isLoading = false
          this.compiledTemplateVariables = JSON.stringify(data.variables)
          this.compiledWhatsappTemplate = data.content
        },
        (error) => {
          this.isLoading = false
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  getGuestWelcomeTemplate(items: WhatsappMessageTemplate[], templateType: 'guest' | 'booking') {
    return items.find((t) => t.isWelcomeMessage && t.templateType === templateType)
  }

  getVendorWelcomeTemplate(items: WhatsappMessageTemplate[]) {
    return items.find((t) => t.isWelcomeMessage && t.templateType === 'vendor')
  }

  close() {
    this.dialogRef.close()
  }

  compileDataTokens(content: string) {
    // Approved WhatsApp template variables must be plain text without newlines (Twilio error 92007).
    return this.dataDictService
      .resolve(
        content,
        {
          inquiry: this.inquiry,
          rental: this.inquiry?.rental,
          guest: this.guest,
        },
        {
          '*|VENDOR:NAME|*': this.vendor?.name || '',
        },
        { plainText: true }
      )
      .pipe(map((text) => text.replace(/\s*\n\s*/g, '; ')))
  }

  compileWhatsappTemplate(template: WhatsappMessageTemplate) {
    if (!isSomething(template.variables)) return of({ content: template.content, variables: {} })

    const content = template.content

    return combineLatest(
      lodash.map(template.variables || [], (tag) => {
        return this.compileDataTokens(tag)
      })
    ).pipe(
      // Render the template with the variables
      map((variables) => ({
        content: content.replace(/{{(\d+)}}/g, (_, key) => variables[key - 1]),
        variables: lodash.mapKeys(
          lodash.mapValues(variables, (t) => t),
          (v, i) => +i + 1
        ),
      }))
    )
  }

  getSentGuestMessage(payload: ChannelMessagePayload) {
    return Message.deserialize({
      inquiry_id: this.inquiry?.id,
      convo_id: payload.convo_id,
      guest_id: payload.guest_id,
      rental_id: payload.rental_id,
      ref_id: this.inquiry?.refId,
      source: this.inquiry?.inquirySource,
      message_txt: this.compiledWhatsappTemplate,
      message_html: this.compiledWhatsappTemplate.replace(/\n/g, '<br>'),
      rental_name: this.inquiry?.rental?.name,
      guest_name: this.guest.name,
      guest_email: this.guest.primaryEmail,
      guest_arrive: this.inquiry?.guestArrive,
      guest_depart: this.inquiry?.guestDepart,
      subject: payload.name,
      from: this.inquiry?.rental?.email,
      type: MessageType.Outbound,
      name: userFullname(this.auth.user),
      received_on: moment().unix(),
      created: moment().unix(),
      pkey: btoa(new Date().toISOString()),
      sent_with: payload.channel,
    })
  }

  send(templateId: string, variables: string, content: string) {
    const channel = 'whatsapp-twilio'
    const payload: ChannelMessagePayload = {
      name: this.inquiry?.rental?.name || userFullname(this.auth.user),
      guest_id: this.guest?.id || this.vendor?.pkey,
      convo_id: this.inquiry?.convoId || this.guest?.id || this.vendor?.pkey,
      inquiry_id: this.inquiry?.id,
      rental_id: this.inquiry?.rentalId,
      channel,
      sender: {
        account: String(this.guest?.account || this.vendor?.account),
      },
      receiver: {
        'phone-number': String(this.guest?.whatsapp_phone || '') || String(this.vendor?.whatsapp_number || ''),
      },
      'message-data': {
        content_id: templateId,
        content_variables: variables,
      },
    }
    return this.msgService.createChannelMessage(payload).pipe(
      tap((response) => {
        if (this.vendor) return

        const message = this.getSentGuestMessage(payload)
        if (this.inquiry) {
          this.store.dispatch(AddMessageComplete({ message }))
        }
      })
    )
  }

  onSend() {
    if (!this.compiledWhatsappTemplate) {
      return
    }

    this.isLoading = true
    this.send(this.whatsappTemplate.sid, this.compiledTemplateVariables, this.compiledWhatsappTemplate)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Message sent successfully`)
          this.dialogRef.close('sent')
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
